/**
 * ErrorSummary.tsx — "what is still missing", said BEFORE and AFTER the press.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Added 2026-09-25 (CTO, on the
 * clinician prescribe form: "till the 'Directions' is filled it does not enable
 * the main CTA to prescribe, but it's not telling it's missed — in case it's
 * missed it's so hard to find").
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE DEFECT: A CONTROL THAT SAYS NO WITHOUT SAYING WHY.
 *
 * The prescribe button used to be `disabled` until every clause of its validity
 * check held, and nothing on the screen named the clause that failed. A
 * disabled button only fades, cannot take focus, and cannot be pressed to ask
 * — so a clinician with one empty box somewhere in a long, multi-line form had
 * to hunt for it (WCAG 3.3.1 / 3.3.2: an error must be identified in text, and
 * the input it concerns described).
 *
 * TWO PIECES, the GOV.UK error pattern plus a quiet "before" line:
 *
 *  - {@link StillNeededHint} — ONE quiet line beside the call to action,
 *    present WHENEVER something is missing, BEFORE any press: "Still needed:
 *    directions (line 1)". Each item is a link that moves focus to the field.
 *    It is the button's `aria-describedby`, so a screen-reader user who lands
 *    on the button hears why it is not ready. It is NOT a live region: it
 *    changes on almost every keystroke that completes or empties a field, and
 *    announcing each of those would drown the form.
 *  - {@link ErrorSummary} — shown when the button is PRESSED while something is
 *    missing: "Before you can prescribe:" and a list of links, one per missing
 *    field, and focus is moved to it. It is the view's ONE `role="alert"`; the
 *    inline error under each field is tied to its control by
 *    `aria-describedby` and is deliberately not a second alert.
 *
 * Every link is a real `<a href="#id">` (GOV.UK's own markup — a link can be
 * read as a list of places to go), whose click is intercepted to FOCUS the
 * field rather than change the URL: the URL is router state in this app, and a
 * fragment is how several screens decide what to open.
 * ═════════════════════════════════════════════════════════════════════════════
 */
import { forwardRef, type CSSProperties, type MouseEvent } from 'react';

/**
 * One thing that stops the form from going ahead.
 *
 * `targets` is a FALLBACK CHAIN of element ids, first found wins: the field
 * itself where it is on screen, otherwise the control that can bring it back
 * (a Retry), otherwise the region it lives in. Built by the form, because only
 * the form knows which of those exist at this instant.
 */
export interface MissingItem {
  /** Stable identity, e.g. `line-0-directions`. */
  key: string;
  /** The short noun phrase for the "Still needed:" line — "directions (line 1)". */
  hint: string;
  /** The full sentence for the summary and the inline error — "Enter directions for line 1". */
  message: string;
  /** Element ids to move focus to, in order of preference. */
  targets: string[];
}

/**
 * Move focus to the first of `targets` that is on the page, and bring it into
 * view. Returns whether anything was focused. `preventScroll` + an explicit
 * centred scroll so the field's LABEL is visible too, not only the box's top
 * edge under a sticky header (jsdom has no `scrollIntoView`, hence the guard).
 */
export function focusFirstTarget(targets: string[]): boolean {
  for (const id of targets) {
    const element = document.getElementById(id);
    if (!element) continue;
    element.focus({ preventScroll: true });
    element.scrollIntoView?.({ block: 'center' });
    return true;
  }
  return false;
}

function goTo(targets: string[]) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    focusFirstTarget(targets);
  };
}

const LINK_STYLE: CSSProperties = {
  color: 'var(--gps-link)',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
};

/**
 * The quiet "before" line. Renders nothing when nothing is missing, so the
 * caller can mount it unconditionally and point `aria-describedby` at `id`
 * only while `items` is non-empty.
 */
export function StillNeededHint({ id, items }: { id: string; items: MissingItem[] }) {
  if (items.length === 0) return null;
  return (
    <p
      id={id}
      data-still-needed=""
      style={{ margin: '8px 0 0', fontSize: '13px', lineHeight: 1.5, color: 'var(--gps-muted)' }}
    >
      Still needed:{' '}
      {items.map((item, index) => (
        <span key={item.key}>
          {index > 0 ? ', ' : null}
          <a href={`#${item.targets[0] ?? ''}`} onClick={goTo(item.targets)} style={LINK_STYLE}>
            {item.hint}
          </a>
        </span>
      ))}
    </p>
  );
}

/**
 * The summary shown after a blocked press. `tabIndex={-1}` so the form can move
 * focus to it; `role="alert"` so it is announced even where focus handling is
 * unreliable. The danger EDGE is a border, and the heading carries the meaning
 * in words — never colour alone.
 */
export const ErrorSummary = forwardRef<
  HTMLDivElement,
  { id: string; title: string; items: MissingItem[] }
>(function ErrorSummary({ id, title, items }, ref) {
  if (items.length === 0) return null;
  const titleId = `${id}-title`;
  return (
    <div
      ref={ref}
      id={id}
      role="alert"
      tabIndex={-1}
      aria-labelledby={titleId}
      data-error-summary=""
      style={{
        border: '2px solid var(--gps-danger-text)',
        borderRadius: 'var(--gps-r-md)',
        padding: '12px 14px',
        margin: '0 0 var(--gps-s-3)',
        background: 'var(--gps-surface)',
        outlineOffset: '2px',
      }}
    >
      <p id={titleId} style={{ margin: '0 0 6px', fontSize: '14.5px', fontWeight: 700, color: 'var(--gps-text)' }}>
        {title}
      </p>
      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: 1.55 }}>
        {items.map((item) => (
          <li key={item.key}>
            <a
              href={`#${item.targets[0] ?? ''}`}
              onClick={goTo(item.targets)}
              style={{ ...LINK_STYLE, color: 'var(--gps-danger-text)', fontWeight: 600 }}
            >
              {item.message}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
});

/**
 * The control's edge while it carries an inline error: the kit's error ink,
 * so the field the summary names is findable at a glance. A SECOND carrier,
 * never the only one — the sentence under the field and `aria-invalid` say
 * it in words (WCAG 1.4.1). The SAME width as the kit's control edge
 * (`CONTROL_STYLE` in kit.tsx, 1.5px), so nothing shifts by a pixel — and the
 * `border` SHORTHAND, because the kit sets the shorthand and React warns (and
 * can mis-apply) when a longhand is added and removed beside one.
 */
export function invalidControlStyle(error: string | undefined): CSSProperties | undefined {
  return error ? { border: '1.5px solid var(--gps-danger-text)' } : undefined;
}

/**
 * The inline error text for one field, or `undefined` — the summary and the
 * inline line say the SAME sentence, so a reader who follows a link lands on
 * the words they just read.
 */
export function inlineErrorFor(items: MissingItem[], key: string): string | undefined {
  return items.find((item) => item.key === key)?.message;
}
